/**
 * @fileoverview Popover for managing indirect cost subcategories (CRUD flows).
 * @module features/indirect-costs/components/SubcategoriesManagerPopover
 */

import { ReactElement, useEffect, useMemo, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Kbd } from '@/components/ui/kbd'
import { Input } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Spinner } from '@/components/ui/spinner'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useIndirectCostSubcategories } from '../hooks/useIndirectCostSubcategories'
import {
  createIndirectCostSubcategory,
  updateIndirectCostSubcategory,
  deleteIndirectCostSubcategory,
  type IndirectCostSubcategoryDTO,
} from '../api/indirectCostSubcategoriesService'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'

interface SubcategoriesManagerPopoverProps {
  onMutated?: () => void
  /** Called after a new subcategory is created, with its UUID */
  onSubcategoryCreated?: (subcategoryUuid: string) => void
  triggerButtonClassName?: string
  /** Controlled open state - when provided, component becomes controlled */
  open?: boolean
  /** Callback for controlled mode */
  onOpenChange?: (open: boolean) => void
  /** Hide the trigger button (useful for controlled mode with external trigger) */
  hideTrigger?: boolean
  /** Custom trigger element (replaces default button) */
  trigger?: ReactElement
}

/**
 * Popover component that allows admins to create, edit, and delete indirect cost subcategories.
 */
export default function SubcategoriesManagerPopover({
  onMutated,
  onSubcategoryCreated,
  triggerButtonClassName = '',
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  hideTrigger = false,
  trigger,
}: SubcategoriesManagerPopoverProps): ReactElement {
  const { t } = useTranslation('features-indirect-costs')
  const queryClient = useQueryClient()
  const [internalOpen, setInternalOpen] = useState(false)

  // Support both controlled and uncontrolled modes
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  // Handle open state changes, resetting form state when closing
  // This avoids cascading renders from setState in useEffect
  const handleOpenChange = (value: boolean): void => {
    if (!value) {
      // Reset all form state when closing
      setIsCreating(false)
      setNewItemName('')
      setEditingId(null)
      setEditValue('')
      setSearch('')
      setDebouncedSearch('')
    }
    if (isControlled) {
      controlledOnOpenChange?.(value)
    } else {
      setInternalOpen(value)
    }
  }
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [newItemName, setNewItemName] = useState('')
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<IndirectCostSubcategoryDTO | null>(null)
  
    useEffect(() => {
      const handle = window.setTimeout(() => {
        setDebouncedSearch(search.trim())
      }, 250)
      return () => window.clearTimeout(handle)
    }, [search])

    const options = useMemo(() => {
      const base: Parameters<typeof useIndirectCostSubcategories>[0] = {}
      if (debouncedSearch) {
        base.search = debouncedSearch
      }
      return base
    }, [debouncedSearch])
  
    const {
      data: subcategories = [],
      isLoading,
      isError,
      error,
    } = useIndirectCostSubcategories(options)
      const visibleSubcategories = useMemo(
        () =>
          [...subcategories]
            .filter(item => !item.system)
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
        [subcategories]
      )  
    const invalidate = async (): Promise<void> => {
      await queryClient.invalidateQueries({ queryKey: ['indirect-costs', 'subcategories'] })
      onMutated?.()
    }
  
    const createMutation = useMutation({
      mutationFn: createIndirectCostSubcategory,
      onSuccess: async (data) => {
        await invalidate()
        onSubcategoryCreated?.(data.uuid)
        setIsCreating(false)
        setNewItemName('')
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : t('subcategories.toasts.createFailed'))
      },
    })
  
    const updateMutation = useMutation({
      mutationFn: async (payload: {
        uuid: string
        body: Parameters<typeof updateIndirectCostSubcategory>[1]
      }) => updateIndirectCostSubcategory(payload.uuid, payload.body),
      onSuccess: async () => {
        toast.success(t('subcategories.toasts.updated'))
        await invalidate()
        setEditingId(null)
        setEditValue('')
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : t('subcategories.toasts.updateFailed'))
      },
    })
  
    const deleteMutation = useMutation({
      mutationFn: deleteIndirectCostSubcategory,
      onSuccess: async () => {
        toast.success(t('subcategories.toasts.deleted'))
        toast.info(t('subcategories.toasts.reassigned'))
        await invalidate()
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : t('subcategories.toasts.deleteFailed'))
      },
    })

    const autoCreateMutation = useMutation({
      mutationFn: async (payload: { uuid: string; autoCreateMonthly: boolean }) =>
        updateIndirectCostSubcategory(payload.uuid, { autoCreateMonthly: payload.autoCreateMonthly }),
      onSuccess: async () => {
        await invalidate()
      },
      onError: (err: unknown) => {
        toast.error(err instanceof Error ? err.message : t('subcategories.toasts.updateFailed'))
      },
    })
  
    const handleCreate = (): void => {
      const trimmed = newItemName.trim()
      if (!trimmed) {
        setIsCreating(false)
        setNewItemName('')
        return
      }
      createMutation.mutate({
        displayName: trimmed,
        category: 'OPEX',
      })
    }
  
    const handleCreateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        handleCreate()
      } else if (e.key === 'Escape') {
        setIsCreating(false)
        setNewItemName('')
      }
    }
  
    const startInlineEdit = (item: IndirectCostSubcategoryDTO): void => {
      setEditingId(item.uuid)
      setEditValue(item.displayName)
      setIsCreating(false)
    }
  
    const handleInlineSave = (): void => {
      if (!editingId) return
      const trimmed = editValue.trim()
      if (!trimmed) {
         setEditingId(null)
         return
      }
  
      const original = visibleSubcategories.find(i => i.uuid === editingId)
      if (original && original.displayName !== trimmed) {
        updateMutation.mutate({ uuid: editingId, body: { displayName: trimmed } })
      } else {
        setEditingId(null)
      }
    }
  
    const handleInlineKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
      if (e.key === 'Enter') {
        handleInlineSave()
      } else if (e.key === 'Escape') {
        setEditingId(null)
        setEditValue('')
      }
    }
  
    const handleAutoCreateToggle = (item: IndirectCostSubcategoryDTO, checked: boolean): void => {
      autoCreateMutation.mutate({ uuid: item.uuid, autoCreateMonthly: checked })
    }
  
    const actionDisabled =
      updateMutation.isPending || createMutation.isPending || deleteMutation.isPending || autoCreateMutation.isPending
  
    return (
      <>
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {trigger ?? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={cn(
                'gap-2',
                triggerButtonClassName,
                hideTrigger && 'sr-only'
              )}
              tabIndex={hideTrigger ? -1 : undefined}
              aria-hidden={hideTrigger}
            >
              {t('subcategories.button')}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent
          className="w-[600px] p-0 rounded-lg shadow-lg"
          align="end"
          sideOffset={8}
          side="bottom"
        >
          <div className="p-4 border-b border-border flex items-center justify-between gap-3">
                      <div>
                        <h2 className="text-foreground font-semibold text-lg">{t('subcategories.title')}</h2>
                      </div>            {(search || subcategories.length >= 8) && (
              <div className="flex items-center gap-2">
                <Input
                  value={search}
                  onChange={event => setSearch(event.target.value)}
                  placeholder={t('subcategories.searchPlaceholder')}
                  className="w-48"
                />
              </div>
            )}
          </div>
  
          <div className="max-h-[420px] overflow-auto scrollbar-elegant">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner className="size-5" />
            </div>
          ) : isError ? (
            <div className="px-6 py-10 text-center text-sm text-destructive">
              {error instanceof Error ? error.message : t('subcategories.states.loadError')}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-14 text-center">#</TableHead>
                  <TableHead>{t('subcategories.table.name')}</TableHead>
                  <TableHead className="w-20 text-center">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-help">{t('subcategories.table.auto')}</span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs">
                        {t('subcategories.autoCreateTooltip')}
                      </TooltipContent>
                    </Tooltip>
                  </TableHead>
                  <TableHead className="text-right">{t('subcategories.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => {
                    if (!isCreating) {
                      setIsCreating(true)
                      setNewItemName('')
                    }
                  }}
                >
                  <TableCell className="text-center text-muted-foreground w-14">
                    <Plus className="size-4 mx-auto" />
                  </TableCell>
                  <TableCell colSpan={3}>
                    {isCreating ? (
                      <Input
                        value={newItemName}
                        onChange={(e) => setNewItemName(e.target.value)}
                        onBlur={handleCreate}
                        onKeyDown={handleCreateKeyDown}
                        autoFocus
                        className="h-8 w-full"
                        placeholder={t('subcategories.form.displayNamePlaceholder')}
                      />
                    ) : (
                      <span className="text-sm text-muted-foreground italic">
                        {t('subcategories.actions.add')}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
                {visibleSubcategories.length === 0 && debouncedSearch && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">
                      {t('subcategories.states.noResults')}
                    </TableCell>
                  </TableRow>
                )}
                {visibleSubcategories.map((item, index) => (
                  <TableRow key={item.uuid}>
                    <TableCell className="text-center text-sm text-muted-foreground">
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col h-9 justify-center">
                        {editingId === item.uuid ? (
                          <Input
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onBlur={handleInlineSave}
                            onKeyDown={handleInlineKeyDown}
                            autoFocus
                            className="h-8"
                          />
                        ) : (
                          <span
                            className="font-medium text-foreground cursor-pointer hover:underline underline-offset-4 decoration-muted-foreground/50"
                            onClick={() => startInlineEdit(item)}
                          >
                            {item.displayName}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={item.autoCreateMonthly}
                        onCheckedChange={(checked) => handleAutoCreateToggle(item, checked)}
                        disabled={actionDisabled}
                        aria-label={t('subcategories.aria.autoCreateToggle')}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled={actionDisabled}
                        onClick={() => setDeleteTarget(item)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </PopoverContent>
    </Popover>

    <Dialog open={deleteTarget !== null} onOpenChange={(isOpen) => !isOpen && setDeleteTarget(null)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {t('deleteConfirm.title', { subcategory: deleteTarget?.displayName })}
          </DialogTitle>
        </DialogHeader>
        <DialogFooter>
          <DialogClose asChild>
            <button type="button" className="glass-button rounded-md px-4 py-2 text-sm font-medium flex items-center gap-2">
              {t('subcategories.actions.cancel')}
              <Kbd>Esc</Kbd>
            </button>
          </DialogClose>
          <button
            type="button"
            className="rounded-md bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 flex items-center gap-2"
            onClick={() => {
              if (deleteTarget) {
                deleteMutation.mutate(deleteTarget.uuid)
              }
              setDeleteTarget(null)
            }}
            autoFocus
          >
            {t('subcategories.actions.delete')}
            <Kbd className="bg-destructive-foreground/10 text-destructive-foreground/70">↵</Kbd>
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  )
}
